import React, { useState } from 'react';
import { ShieldCheck, CheckCircle, Upload, Award, DollarSign, Users, ArrowRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';

export const BecomeSeller: React.FC = () => {
  const { user } = useAuth();
  const [storeName, setStoreName] = useState('');
  const [games, setGames] = useState('Valorant, League of Legends');
  const [experience, setExperience] = useState('2+ years selling on digital marketplaces with 500+ trades');
  const [idFile, setIdFile] = useState<File | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-12">
      
      {/* Header */}
      <div className="text-center max-w-2xl mx-auto space-y-3">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-brand-blue/10 text-brand-blue dark:text-brand-accent">
          <Award className="w-3.5 h-3.5" />
          <span>Verified Merchant Program</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-black text-brand-lightText dark:text-brand-darkText">
          Become a Verified GameVault Seller
        </h1>
        <p className="text-xs sm:text-sm text-brand-lightSecText dark:text-brand-darkSecText leading-relaxed">
          Reach thousands of active gaming enthusiasts with our low 8% commission rate, instant automated stock delivery, and fast payouts.
        </p>
      </div>

      {/* Perks Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <div className="p-6 rounded-3xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder text-center space-y-2">
          <DollarSign className="w-8 h-8 text-emerald-500 mx-auto" />
          <h3 className="font-bold text-sm">Low 8% Flat Fee</h3>
          <p className="text-xs text-brand-lightSecText dark:text-brand-darkSecText">Keep 92% of your revenue with transparent ledger tracking.</p>
        </div>

        <div className="p-6 rounded-3xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder text-center space-y-2">
          <ShieldCheck className="w-8 h-8 text-brand-blue mx-auto" />
          <h3 className="font-bold text-sm">Chargeback Shield</h3>
          <p className="text-xs text-brand-lightSecText dark:text-brand-darkSecText">We absorb unauthorized claims for completed verified deliveries.</p>
        </div>

        <div className="p-6 rounded-3xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder text-center space-y-2">
          <Users className="w-8 h-8 text-purple-500 mx-auto" />
          <h3 className="font-bold text-sm">Automated Vault Delivery</h3>
          <p className="text-xs text-brand-lightSecText dark:text-brand-darkSecText">Upload encrypted keys in bulk. Rest while your store sells 24/7.</p>
        </div>
      </div>

      {/* Application Form */}
      <div className="p-8 rounded-3xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder shadow-lg">
        {submitted ? (
          <div className="text-center py-10 space-y-4">
            <div className="w-16 h-16 rounded-full bg-emerald-500/15 text-emerald-500 mx-auto flex items-center justify-center">
              <CheckCircle className="w-8 h-8" />
            </div>
            <h3 className="text-xl font-bold text-brand-lightText dark:text-brand-darkText">
              Seller Application Received
            </h3>
            <p className="text-xs text-brand-lightSecText dark:text-brand-darkSecText max-w-md mx-auto leading-relaxed">
              Our vendor onboarding specialists are reviewing your submitted trade history and store details. You will be notified via email within 24 business hours.
            </p>
            <Link
              to="/"
              className="inline-block px-6 py-2.5 rounded-xl font-bold text-xs text-white bg-brand-blue"
            >
              Return to Marketplace
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <h2 className="font-extrabold text-lg text-brand-lightText dark:text-brand-darkText">
              Seller Application Form
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs font-semibold mb-1">Store / Business Name</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Apex Vault Store"
                  value={storeName}
                  onChange={(e) => setStoreName(e.target.value)}
                  className="w-full p-3 text-xs rounded-xl bg-brand-lightSec dark:bg-brand-darkElevated border border-brand-lightBorder dark:border-brand-darkBorder"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold mb-1">Primary Game Titles</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Valorant, CS2, WoW"
                  value={games}
                  onChange={(e) => setGames(e.target.value)}
                  className="w-full p-3 text-xs rounded-xl bg-brand-lightSec dark:bg-brand-darkElevated border border-brand-lightBorder dark:border-brand-darkBorder"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold mb-1">Trading Experience & References</label>
              <textarea
                required
                rows={3}
                value={experience}
                onChange={(e) => setExperience(e.target.value)}
                placeholder="Mention past platforms, trade volume, or verified reviews..."
                className="w-full p-3 text-xs rounded-xl bg-brand-lightSec dark:bg-brand-darkElevated border border-brand-lightBorder dark:border-brand-darkBorder"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold mb-1">Government ID / Business Verification</label>
              <div className="p-6 border-2 border-dashed border-brand-lightBorder dark:border-brand-darkBorder rounded-2xl text-center cursor-pointer hover:border-brand-blue">
                <Upload className="w-8 h-8 text-brand-lightSecText mx-auto mb-2" />
                <p className="text-xs font-bold text-brand-lightText dark:text-brand-darkText">
                  {idFile ? idFile.name : 'Upload Driver License / Passport / Business Registration'}
                </p>
                <p className="text-[11px] text-brand-lightSecText mt-1">PNG, JPG, or PDF up to 10MB (Protected in Private R2 Vault)</p>
                <input
                  type="file"
                  className="hidden"
                  id="id-upload"
                  onChange={(e) => e.target.files && setIdFile(e.target.files[0])}
                />
                <label htmlFor="id-upload" className="mt-3 inline-block px-4 py-1.5 rounded-lg text-xs font-bold bg-brand-lightSec dark:bg-brand-darkElevated cursor-pointer">
                  Browse File
                </label>
              </div>
            </div>

            <button
              type="submit"
              className="w-full py-3.5 rounded-2xl font-bold text-xs text-white bg-brand-blue hover:bg-brand-hover shadow-glow-blue flex items-center justify-center gap-2 transition-all"
            >
              <span>Submit Application for Review</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        )}
      </div>
    </div>
  );
};
